import React, { useEffect, useCallback, useReducer } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, TextField, IconButton, InputAdornment, Grid, TableSortLabel, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { SortingOrder } from '../../models/enums/sortingOrder';
import { ROUTES } from '../../constants/routes';
import TmPlus from '../../icons/TmPlus';
import { CategoryType } from '../../models/enums/categoryType';
import { PermissionType } from '../../models/enums/permissionType';
import { ShowForPermission } from '../common/permissions/ShowForPermission';
import { CompanyTableViewDto } from '../../models/companies/companyTableViewDto';
import { companiesService } from '../../services/companiesService';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';
import AcceptLabel from '../common/labels/AcceptLabel';
import DenyLabel from '../common/labels/DenyLabel';
import TelemedLabel from '../common/labels/TelemedLabel';

const PAGE_REFRESH_INTERVAL = 10000;

const initialState = {
    companies: [] as CompanyTableViewDto[],
    page: 0,
    rowsPerPage: 15,
    dataCount: 0,
    loading: true,
    sortColumn: 'id',
    sortOrder: SortingOrder.Asc,
    loadingColumn: null as string | null,
    filter: {
        name: '',
        inn: '',
    },
    shouldFetch: true,
};

type State = typeof initialState;

type Action =
    | { type: 'SET_COMPANIES'; payload: { companies: CompanyTableViewDto[]; dataCount: number } }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_ROWS_PER_PAGE'; payload: number }
    | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
    | { type: 'SET_SORT'; payload: { column: string; order: SortingOrder } }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_SHOULD_FETCH'; payload: boolean };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_COMPANIES':
            return {
                ...state,
                companies: action.payload.companies,
                dataCount: action.payload.dataCount,
                loading: false,
                loadingColumn: null,
                shouldFetch: false,
            };
        case 'SET_PAGE':
            return { ...state, page: action.payload, loading: true, shouldFetch: true };
        case 'SET_ROWS_PER_PAGE':
            return { ...state, rowsPerPage: action.payload, page: 0, loading: true, shouldFetch: true };
        case 'SET_FILTER':
            return {
                ...state,
                filter: { ...state.filter, ...action.payload },
                page: 0,
                loading: true,
                shouldFetch: true,
            };
        case 'SET_SORT':
            return {
                ...state,
                sortColumn: action.payload.column,
                sortOrder: action.payload.order,
                loading: true,
                loadingColumn: action.payload.column,
                shouldFetch: true,
            };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_SHOULD_FETCH':
            return { ...state, shouldFetch: action.payload };
        default:
            return state;
    }
};

export const CompaniesTable = () => {
    const [state, dispatch] = useReducer(reducer, initialState, (baseState) => ({
        ...baseState,
        filter: filterStorageService.loadFilter(FilterSection.Clients, baseState.filter),
    }));

    const navigate = useNavigate();

    const fetchCompanies = useCallback(() => {
        if (!state.shouldFetch) return;

        dispatch({ type: 'SET_LOADING', payload: true });

        companiesService.getByFilter({
            page: state.page,
            pageSize: state.rowsPerPage,
            name: state.filter.name || undefined,
            inn: state.filter.inn,
            sortColumn: state.sortColumn,
            sortOrder: state.sortOrder,
        })
            .then(r => {
                dispatch({
                    type: 'SET_COMPANIES',
                    payload: {
                        companies: r.data.data.items,
                        dataCount: r.data.data.count,
                    },
                });
            })
            .catch(e => console.log(e))
            .finally(() => {
                dispatch({ type: 'SET_LOADING', payload: false });
            });
    }, [state.shouldFetch, state.page, state.rowsPerPage, state.sortColumn, state.sortOrder, state.filter]);

    useEffect(() => {
        if (state.shouldFetch) {
            fetchCompanies();
        }
        const interval = setInterval(fetchCompanies, PAGE_REFRESH_INTERVAL);
        return () => clearInterval(interval); // Clear interval on component unmount
    }, [state.shouldFetch, fetchCompanies]);

    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.Clients, state.filter);
    }, [state.filter]);

    const handleChangePage = (_event: any, newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        dispatch({ type: 'SET_FILTER', payload: { [name]: value } as Partial<State['filter']> });
    };

    const handleRowClick = (event: React.MouseEvent, id: number) => {
        const url = ROUTES.clients.idPath.replace(':id', String(id));
        navigate(url);
    };

    const handleAddClient = () => {
        navigate(ROUTES.clients.add);
    };

    const handleSort = (column: string) => {
        const isAsc = state.sortColumn === column && state.sortOrder === SortingOrder.Asc;
        dispatch({
            type: 'SET_SORT',
            payload: { column, order: isAsc ? SortingOrder.Desc : SortingOrder.Asc },
        });
    };

    const renderTableSortLabel = (column: string, label: string) => (
        <TableSortLabel
            active={state.sortColumn === column}
            direction={state.sortColumn === column ? state.sortOrder : SortingOrder.Asc}
            onClick={() => handleSort(column)}
            IconComponent={state.loadingColumn === column ? () => <CircularProgress size={12} sx={{ color: '#9588e8' }} /> : undefined}
        >
            <Typography variant="body1" sx={{ color: "#7c7e92" }}>{label}</Typography>
        </TableSortLabel>
    );

    return (
        <Box>
            <Grid container pb={1} mt={2.5}>
                <Grid item xs={10} style={{ display: 'flex', alignItems: 'center' }}>

                    <TextField
                        label="Название компании"
                        name="name"
                        value={state.filter.name}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 230, marginRight: 10 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: "10px",
                            }
                        }}
                    />

                    <TextField
                        label="ИНН"
                        name="inn"
                        value={state.filter.inn}
                        onChange={handleFilterChange}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{ width: 230, marginRight: 10 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: "10px",
                            }
                        }}
                    />

                </Grid>

                <Grid item xs={2} style={{ display: 'inline-block', alignItems: 'center' }}>
                    <ShowForPermission catalog={CategoryType.Companies} permission={PermissionType.Add}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton sx={{ display: "flex" }} className="list-item" onClick={handleAddClient}>
                                <TmPlus fill="none" stroke="white" />&nbsp;
                                <Typography fontWeight="bold" pr={1}>
                                    &nbsp;Добавить клиента
                                </Typography>
                            </IconButton>
                        </Box>
                    </ShowForPermission>
                </Grid>
            </Grid>

            <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 } }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('id', 'ID')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('name', 'Название компании')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('inn', 'ИНН')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('dispatchersCount', 'Кол-во диспетчеров')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('agreementState', 'Договор')}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                                {renderTableSortLabel('isTelemed', 'Группа')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.companies.map((company) => (
                            <TableRow
                                key={company.id}
                                onClick={(event) => handleRowClick(event, company.id)}
                                sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{company.id}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{company.name}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{company.inn}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{company.dispatchersCount}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {
                                        company.agreementState?.isValid
                                            ? <AcceptLabel>Действует</AcceptLabel>
                                            : <DenyLabel>Заблокирован</DenyLabel>
                                    }
                                </TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {company.isTelemed ? <TelemedLabel /> : ""}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Всего: {state.dataCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={state.dataCount}
                    rowsPerPage={state.rowsPerPage}
                    page={state.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={"Строк на странице:"}
                    labelDisplayedRows={({ from, to, count }: any) => `Показано ${from}-${to} из ${count}`}
                />
            </Box>
        </Box>
    );
};
